// useSchedulerViewData.js

import { useCallback, useEffect, useMemo, useState } from 'react';
import { endOfDay, startOfDay } from 'date-fns';

// Context
import { useNodeTypes } from 'context/NodeTypesContext';
import { useUser } from 'context/UserContext';
import { useBase } from 'context/BaseContext';

// Services
import { loadBaseCalendarEventItems, subscribeBaseCalendarEventItems } from 'services/events/BaseCalendarEventService';
import { listNodesByKeys, subscribeToNodesByKeys } from 'services/nodes/NodeReadService';
import { listAdjacentNodeKeys, subscribeAdjacentNodeKeys } from 'services/nodes/NodeEdgeService';

// Utilities
import { normalizeDateValue } from 'utilities/DateTime';
import { resolveDisplaySchemaFields } from 'views/utils/displaySchemaFields';
import { resolveSchemaFieldValue } from 'views/utils/fieldValueResolver';
import { createFilterSortEvaluator } from 'views/utils/filterSortEvaluator';

const getNodeTypeKey = (node) => String(node?.type || node?.typeKey || '').trim().toUpperCase();

const parseTemporalRange = (value, fieldType) => {
    const normalizedType = String(fieldType || '').toLowerCase();

    if (value == null) return null;

    let startCandidate = null;
    let endCandidate = null;

    if (typeof value === 'object') {
        startCandidate = value?.startDate ?? value?.start ?? value?.from ?? value?.date ?? value?.dateTime;
        endCandidate = value?.endDate ?? value?.end ?? value?.to ?? null;
    } else {
        startCandidate = value;
    }

    if (normalizedType === 'date' || normalizedType === 'datetime') {
        const point = normalizeDateValue(startCandidate ?? value);
        if (!point) return null;
        return {
            start: point,
            end: point,
        };
    }

    const start = normalizeDateValue(startCandidate);
    if (!start) return null;

    const parsedEnd = normalizeDateValue(endCandidate);
    const end = parsedEnd && parsedEnd.getTime() >= start.getTime()
        ? parsedEnd
        : start;

    return {
        start,
        end,
    };
};

const rangeOverlapsDay = (range, dayStart, dayEnd) => {
    const startMs = range?.start instanceof Date ? range.start.getTime() : 0;
    const endMs = range?.end instanceof Date ? range.end.getTime() : startMs;
    const dayStartMs = dayStart instanceof Date ? dayStart.getTime() : 0;
    const dayEndMs = dayEnd instanceof Date ? dayEnd.getTime() : dayStartMs;

    if (!startMs || !dayStartMs || !dayEndMs) return false;

    return startMs <= dayEndMs && endMs >= dayStartMs;
};

/**
 * useSchedulerViewData
 *
 * Loads and joins scheduled nodes for the selected scheduler day.
 *
 * @param {object} params - Hook parameters.
 * @param {object} params.view - Active scheduler view definition.
 * @param {object[]} params.resourceNodes - Visible resource nodes for the current view.
 * @param {Date} params.selectedDay - The currently selected day.
 *
 * @returns {object} Scheduler runtime data and loading state.
 */
const useSchedulerViewData = ({ view, resourceNodes = [], selectedDay }) => {
    const { activeBase } = useBase();
    const { getType } = useNodeTypes();
    const { user } = useUser();

    const [state, setState] = useState({
        isLoading: false,
        error: '',
        scheduledEntries: [],
    });
    const [liveRevision, setLiveRevision] = useState(0);

    const schedulerConfig = useMemo(() => (
        view?.schedulerConfig && typeof view.schedulerConfig === 'object'
            ? view.schedulerConfig
            : {}
    ), [view?.schedulerConfig]);

    const baseKey = String(view?.baseKey || activeBase?.key || '').trim();
    const rawScheduledTypeKey = String(schedulerConfig?.scheduledType || '').trim();
    const scheduledTypeKey = rawScheduledTypeKey.toUpperCase();
    const scheduledFieldKey = String(schedulerConfig?.scheduledField || '').trim();
    const scheduledFilters = useMemo(() => (
        Array.isArray(schedulerConfig?.scheduledFilters) ? schedulerConfig.scheduledFilters : []
    ), [schedulerConfig?.scheduledFilters]);
    const safeResourceNodes = useMemo(() => (
        Array.isArray(resourceNodes) ? resourceNodes.filter((node) => node?.key) : []
    ), [resourceNodes]);

    const scheduledTypeDef = useMemo(() => {
        if (!rawScheduledTypeKey) return null;
        return getType(rawScheduledTypeKey) || null;
    }, [getType, rawScheduledTypeKey]);

    const scheduledSchemaFields = useMemo(() => {
        if (!rawScheduledTypeKey) return [];
        return resolveDisplaySchemaFields({ key: rawScheduledTypeKey }, { includeHidden: true });
    }, [rawScheduledTypeKey]);

    const scheduledSchemaFieldMap = useMemo(() => {
        const map = new Map();
        scheduledSchemaFields.forEach((field) => {
            const fieldKey = field?.name || field?.key || '';
            if (fieldKey) map.set(fieldKey, field);
        });
        return map;
    }, [scheduledSchemaFields]);

    const scheduledFieldDef = useMemo(() => {
        if (!scheduledFieldKey) return null;
        return scheduledSchemaFieldMap.get(scheduledFieldKey) || null;
    }, [scheduledFieldKey, scheduledSchemaFieldMap]);

    const resolveScheduledFieldValue = useCallback((node, fieldDef) => {
        return resolveSchemaFieldValue({
            item: node,
            fieldDef,
            typeKey: scheduledTypeDef?.key,
            dataSection: scheduledTypeDef?.dataSection,
        });
    }, [scheduledTypeDef?.dataSection, scheduledTypeDef?.key]);

    const { applyFilters } = useMemo(() => (
        createFilterSortEvaluator({
            resolveFieldValue: resolveScheduledFieldValue,
            schemaFieldMap: scheduledSchemaFieldMap,
            viewerUserKey: user?.key,
        })
    ), [resolveScheduledFieldValue, scheduledSchemaFieldMap, user?.key]);

    useEffect(() => {
        if (!baseKey || !scheduledTypeKey || !scheduledFieldKey || !scheduledFieldDef || !safeResourceNodes.length) {
            return undefined;
        }

        let isDisposed = false;
        let refreshTimeoutId = null;
        const dayStart = startOfDay(selectedDay);
        const dayEnd = endOfDay(selectedDay);
        const unsubscribeCallbacks = [];
        const adjacentNodeKeysByResourceKey = new Map();
        let unsubscribeScheduledNodes = () => {};
        let subscribedScheduledNodeKeySignature = '';

        const scheduleReload = (reason, payload = {}) => {
            if (isDisposed) return;

            if (refreshTimeoutId !== null) return;

            refreshTimeoutId = setTimeout(() => {
                refreshTimeoutId = null;
                setLiveRevision((prev) => prev + 1);
            }, 0);
        };

        const refreshScheduledNodeSubscriptions = () => {
            const scheduledNodeKeys = Array.from(new Set(
                Array.from(adjacentNodeKeysByResourceKey.values())
                    .flatMap((keys) => Array.isArray(keys) ? keys : [])
                    .map((key) => String(key || '').trim())
                    .filter(Boolean)
            ));
            const nextSignature = scheduledNodeKeys.join('|');

            if (nextSignature === subscribedScheduledNodeKeySignature) {
                return;
            }

            unsubscribeScheduledNodes();
            subscribedScheduledNodeKeySignature = nextSignature;

            if (!scheduledNodeKeys.length) {
                unsubscribeScheduledNodes = () => {};
                return;
            }

            unsubscribeScheduledNodes = subscribeToNodesByKeys(scheduledNodeKeys, () => {
                scheduleReload('scheduled-node-docs', {
                    nodeCount: scheduledNodeKeys.length,
                });
            });
        };

        unsubscribeCallbacks.push(
            subscribeBaseCalendarEventItems({
                baseKey,
                windowStartMs: dayStart.getTime(),
                windowEndMs: dayEnd.getTime(),
            }, (eventItems) => {
                scheduleReload('calendar-events', {
                    eventCount: Array.isArray(eventItems) ? eventItems.length : 0,
                });
            })
        );

        safeResourceNodes.forEach((node) => {
            unsubscribeCallbacks.push(
                subscribeAdjacentNodeKeys(node.key, (neighborKeys) => {
                    adjacentNodeKeysByResourceKey.set(String(node.key || '').trim(), neighborKeys);
                    refreshScheduledNodeSubscriptions();

                    scheduleReload('resource-adjacency', {
                        adjacentCount: Array.isArray(neighborKeys) ? neighborKeys.length : 0,
                        resourceKey: String(node.key || '').trim(),
                    });
                }, { baseKey })
            );
        });

        return () => {
            isDisposed = true;

            if (refreshTimeoutId !== null) {
                clearTimeout(refreshTimeoutId);
            }

            unsubscribeScheduledNodes();

            unsubscribeCallbacks.forEach((unsubscribe) => {
                if (typeof unsubscribe === 'function') {
                    unsubscribe();
                }
            });
        };
    }, [safeResourceNodes, scheduledFieldDef, scheduledFieldKey, scheduledTypeKey, selectedDay, baseKey]);

    useEffect(() => {
        let isCancelled = false;

        const loadSchedulerData = async () => {
            if (!baseKey || !scheduledTypeKey || !scheduledFieldKey || !scheduledFieldDef || !safeResourceNodes.length) {
                setState({
                    isLoading: false,
                    error: '',
                    scheduledEntries: [],
                });
                return;
            }

            setState((prev) => ({
                ...prev,
                isLoading: true,
                error: '',
            }));

            try {
                const dayStart = startOfDay(selectedDay);
                const dayEnd = endOfDay(selectedDay);
                const resourceLinkRows = await Promise.all(safeResourceNodes.map(async (node) => ({
                    adjacentKeys: await listAdjacentNodeKeys(node.key, { baseKey }),
                    resourceKey: String(node.key).trim(),
                })));

                const resourceKeysByScheduledNode = new Map();

                resourceLinkRows.forEach(({ adjacentKeys, resourceKey }) => {
                    (Array.isArray(adjacentKeys) ? adjacentKeys : []).forEach((adjacentKey) => {
                        const scheduledNodeKey = String(adjacentKey || '').trim();
                        if (!scheduledNodeKey) return;

                        const existing = resourceKeysByScheduledNode.get(scheduledNodeKey) || new Set();
                        existing.add(resourceKey);
                        resourceKeysByScheduledNode.set(scheduledNodeKey, existing);
                    });
                });

                const eventItems = await loadBaseCalendarEventItems({
                    baseKey,
                    windowStartMs: dayStart.getTime(),
                    windowEndMs: dayEnd.getTime(),
                });

                const eventBackedNodes = Array.from(new Map(
                    eventItems
                        .filter((item) => {
                            const nodeKey = String(item?.node?.key || '').trim();
                            const fieldKey = String(item?.field?.name || '').trim();
                            return Boolean(nodeKey)
                                && getNodeTypeKey(item?.node) === scheduledTypeKey
                                && fieldKey === scheduledFieldKey;
                        })
                        .map((item) => [item.node.key, item.node])
                ).values());

                const edgeBackedNodes = resourceKeysByScheduledNode.size > 0
                    ? await listNodesByKeys(Array.from(resourceKeysByScheduledNode.keys()))
                    : [];

                const scheduledNodes = Array.from(new Map(
                    [...eventBackedNodes, ...edgeBackedNodes]
                        .filter((node) => getNodeTypeKey(node) === scheduledTypeKey)
                        .map((node) => [node.key, node])
                ).values());

                const filteredNodes = scheduledFilters.length
                    ? scheduledNodes.filter((node) => applyFilters(node, scheduledFilters))
                    : scheduledNodes;

                const joinedEntries = filteredNodes.map((node) => {
                    const nodeKey = String(node?.key || '').trim();
                    if (!nodeKey) return null;

                    const resourceKeys = Array.from(resourceKeysByScheduledNode.get(nodeKey) || []);
                    if (!resourceKeys.length) return null;

                    const rawValue = resolveScheduledFieldValue(node, scheduledFieldDef);
                    const range = parseTemporalRange(rawValue, scheduledFieldDef?.type);
                    if (!range?.start || !range?.end || !rangeOverlapsDay(range, dayStart, dayEnd)) return null;

                    return {
                        key: nodeKey,
                        node,
                        range,
                        resourceKeys,
                    };
                });

                if (isCancelled) return;

                setState({
                    isLoading: false,
                    error: '',
                    scheduledEntries: joinedEntries
                        .filter(Boolean)
                        .sort((left, right) => {
                            const startDelta = left.range.start.getTime() - right.range.start.getTime();
                            if (startDelta !== 0) return startDelta;
                            return String(left.node?.title || '').localeCompare(String(right.node?.title || ''), undefined, { sensitivity: 'base' });
                        }),
                });
            } catch (error) {
                if (isCancelled) return;

                console.error('[SchedulerView] load scheduler data error:', error);
                setState({
                    isLoading: false,
                    error: 'Unable to load scheduled records for this day.',
                    scheduledEntries: [],
                });
            }
        };

        loadSchedulerData();

        return () => {
            isCancelled = true;
        };
    }, [
        applyFilters,
        resolveScheduledFieldValue,
        safeResourceNodes,
        scheduledFieldDef,
        scheduledFieldKey,
        scheduledFilters,
        scheduledTypeKey,
        selectedDay,
        liveRevision,
        baseKey,
    ]);

    return {
        baseKey,
        scheduledTypeDef,
        scheduledFieldDef,
        scheduledSchemaFieldMap,
        resolveScheduledFieldValue,
        isLoading: state.isLoading,
        error: state.error,
        scheduledEntries: state.scheduledEntries,
    };
};

export default useSchedulerViewData;